import { useRef } from 'react'
import type { CaptureSource } from '@budgetmonkey/shared'

/**
 * Camera capture — REQUIREMENTS.md §6.2, §7.1 steps 2–3.
 *
 * The button that has to work at a restaurant table. `capture="environment"`
 * on a file input is what opens the rear camera directly on iOS and Android;
 * getUserMedia would mean building a viewfinder, a shutter, and an orientation
 * story that the OS camera already does better.
 *
 * §6.2's hard requirement is that this depends on no other app — not Gmail,
 * not Photos. A file input with `capture` is the whole mechanism, which is
 * exactly why it's the right one.
 *
 * On a desktop browser `capture` is ignored and this opens a file picker, so
 * the button is only rendered where a camera plausibly exists.
 */
export function CameraCapture({
  onCapture,
  disabled,
}: {
  onCapture: (files: readonly File[], source: CaptureSource) => void
  disabled?: boolean
}) {
  const inputRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={disabled}
        className="flex min-h-14 w-full items-center justify-center gap-3 rounded-xl bg-slate-900 px-6 text-base font-semibold text-white transition hover:bg-slate-700 disabled:bg-slate-300"
      >
        <CameraIcon />
        New receipt
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        onChange={(event) => {
          const files = Array.from(event.target.files ?? [])
          if (files.length > 0) onCapture(files, 'mobile_capture')
          // Cleared so photographing the same receipt twice in a row still
          // fires a change event.
          event.target.value = ''
        }}
      />
    </>
  )
}

function CameraIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-6"
      aria-hidden="true"
    >
      <path d="M4 7h3l1.5-2h7L17 7h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1Z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  )
}
